import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { AnimationGroup } from '@babylonjs/core/Animations/animationGroup';
import find from 'lodash/find';
import isEqual from 'lodash/isEqual';
import remove from 'lodash/remove';
import cloneDeep from 'lodash/cloneDeep';

import { projectInterface } from '../Tool/interface';
import { IVariantText } from './Pattern/Decal/patternText';
import { IVariantMask } from './Pattern/Decal/patternMask';
import { IContentOptions, vector3Json } from './content';
import { SystemLayer } from '../System/systemLayer';
import { IArcCameraAngles } from '../System/systemCamera';
import { getRandomString } from '../../Utils/math';
import { ContentModelDecal } from './contentModelDecal';
import {
    IVariantMeshes,
    IVariantMeshesMaterials,
    IVariantMeshesProperties,
} from './contentModelVariantMesh';
import { MaterialEvent } from '../Tool/toolMaterial';

export interface IVariantModel {
    name: string
    variantMeshesList: IVariantMeshes[]
    variantTextList?: IVariantText[]
    variantMaskList?: IVariantMask[]
    id?: string
}

export interface IMeshCameraAngles extends IArcCameraAngles {
    name?: string
}

export interface IModelVariants {
    url: string
    variants: IVariantModel[]
}

export interface IModelOptions extends IModelVariants {
    material: string
    variantMeshesPropertiesList: IVariantMeshesProperties[]
    variant: string
}

projectInterface.variantMeshes = {
    meshes: 'me',
    material: 'ma',
};

projectInterface.variant = {
    name: 'n',
    variantMeshesList: {
        dim: 'co',
        next: [projectInterface.variantMeshes],
    },
};

projectInterface.decalGeometry = {
    // For PatternDecal
    name: 'na',
    type: 't',
    sourceMeshName: 'smn',
    sourceMeshStayVisible: 'smsv',
    position: {
        dim: 'p',
        next: vector3Json
    },
    normal: {
        dim: 'n',
        next: vector3Json
    },
    rotation: 'r',
    size: 's',
    // For PatternDecalText
    defaultText: 'tdt',
    maximumCharacter: 'tmc',
    fonts: 'tf',
    fontSize: 'tfs',
    // For PatternDecalMask
    maskList: 'ml',

};

projectInterface.variantMeshesMaterials = {
    meshes: 'me',
    nakerName: 'nn',
    cameraView: 'cv',
    materials: ['ma'],
    decalGeometry: {
        dim: 'gb',
        next: projectInterface.decalGeometry
    },
};

projectInterface.model = {
    url: 'u',
    material: 'ma',
    variant: 'va',
    variants: {
        dim: 'vas',
        next: [projectInterface.variant],
    },
    variantMeshesPropertiesList: {
        dim: 'cmm',
        next: [projectInterface.variantMeshesMaterials],
    },
};

export class ContentModel extends ContentModelDecal {
    public type = 'model'

    public url: string

    public variants: IVariantModel[] = []

    public variant: string

    public keepScaleRatio = true

    constructor(
        systemLayer: SystemLayer,
        content: IContentOptions,
        modelOptions: IModelOptions
    ) {
        super(systemLayer, content);
        this.setTypeParameter(modelOptions);

        systemLayer.toolMaterial.on(MaterialEvent.Update, (material) => {
            this.checkMaterialChange(material.key);
        });
    }

    public setTypeParameter(modelOptions: IModelOptions): void {
        if (modelOptions.url !== undefined) this.loadModel(modelOptions.url);
        if (modelOptions.material) this.setMaterial(modelOptions.material);
        if (modelOptions.variants) this.setVariants(modelOptions.variants);
        if (modelOptions.variant) this.setVariantByName(modelOptions.variant, false);
        if (modelOptions.variantMeshesPropertiesList) {
            this.setVariantMeshesPropertiesList(
                modelOptions.variantMeshesPropertiesList
            );
        }
    }

    public modelLoaded = false

    public loadModel(url: string, callback?: () => void): void {
        this.url = url;
        if (!this.visible) {
            this.modelLoaded = false;
        } else {
            this._system.loader.getModel(
                url,
                (meshes: Mesh[], modelAnimations?: AnimationGroup[]) => {
                    this.modelLoaded = true;
                    if (meshes) {
                        this.setModel(meshes);
                        if (modelAnimations) this.setModelAnimations(modelAnimations);
                        this.addAnimations(this.animations);
                        if (this.material) this.setMaterial(this.material);
                        if (this.variant) this.setVariantByName(this.variant, false);
                        if (this.shown) this.show();
                        else this.hide();
                        // Once patternMeshes created and shown, add the decals
                        this.buildAllPatternDecal();
                    } else {
                        this.patternModel.destroyMeshes();
                    }
                    if (callback) callback();
                }
            );
        }
    }

    private setModel(meshes: Mesh[]) {
        this.patternModel.setMeshes(meshes);
        this.checkExistingVariantMeshesProperties();
    }

    private checkExistingVariantMeshesProperties() {
        // Clone Objet otherwise the loop will be false as we remove element from array
        const variantMeshesPropertiesListCloned = cloneDeep(
            this.variantMeshesPropertiesList
        );
        variantMeshesPropertiesListCloned.forEach((variantMeshesProperties) => {
            if (variantMeshesProperties.meshes.length === 1) {
                const patternMesh = find(this.patternModel.patternMeshes, (ml) => isEqual(
                    [ml.mesh.name],
                    variantMeshesProperties.meshes
                ));
                if (!patternMesh && !variantMeshesProperties.decalGeometry) {
                    remove(this.variantMeshesPropertiesList,
                        (v) => isEqual(v.meshes, variantMeshesProperties.meshes));
                }
            }
        });
        this.patternModel.patternMeshes.forEach((pM) => {
            const meshList = [pM.mesh.name];
            const meshProperties = this.getVariantMeshesPropertiesByMeshes(meshList);
            if (!meshProperties) {
                this.variantMeshesPropertiesList.push({ meshes: meshList });
            } else if (meshProperties.nakerName) {
                pM.setTag(meshProperties.nakerName);
            }
        });
    }

    /* ------------------ MESHESPRROPERTIES ------------------ */

    // Might change it to private
    public setVariantMeshesPropertiesList(
        variantMeshesMaterialsList: IVariantMeshesMaterials[]
    ): void {
        this.variantMeshesPropertiesList = variantMeshesMaterialsList;
    }

    private buildAllPatternDecal(): void {
        this.variantMeshesPropertiesList.forEach((variantMeshesProperties) => {
            if (variantMeshesProperties.decalGeometry) {
                this.buildPatternDecalWithSourceMesh(variantMeshesProperties);
            }
        });
    }

    public getVariantMeshesPropertiesList(): IVariantMeshesProperties[] {
        return cloneDeep(this.variantMeshesPropertiesList);
    }

    public setMeshCameraAngles(meshCameraAngles: IMeshCameraAngles[]): void {
        meshCameraAngles.forEach((cameraView) => {
            if (cameraView.name) this.setMeshCameraAngle(cameraView.name, cameraView);
        });
    }

    public setMeshCameraAngle(nakerName: string, cameraView: IArcCameraAngles): void {
        const meshProperty = find(
            this.variantMeshesPropertiesList,
            (mp) => mp.nakerName === nakerName || mp.meshes[0] === nakerName
        );
        if (meshProperty) {
            meshProperty.cameraView = cameraView;
        } else {
            this.variantMeshesPropertiesList.push({
                meshes: [nakerName],
                nakerName,
                cameraView,
            });
        }
    }

    public setMeshNakerName(meshes: string[], nakerName: string): void {
        const meshProperty = this.getVariantMeshesPropertiesByMeshes(meshes);
        if (meshProperty) {
            meshProperty.nakerName = nakerName;
        } else {
            this.variantMeshesPropertiesList.push({
                meshes,
                nakerName,
            });
        }
        const { decalGeometry } = meshProperty;
        if (decalGeometry) {
            const patternDecal = this.getPatternDecal(meshes);
            if (patternDecal) patternDecal.setGeometryName(nakerName);
            else console.error("Can't find PatternDecal when rename Mesh.");
        }
    }

    public getMeshNakerName(meshes: string[]): string {
        const variantMeshProperties = this.getVariantMeshesPropertiesByMeshes(meshes);
        return variantMeshProperties && variantMeshProperties.nakerName
            ? variantMeshProperties.nakerName
            : meshes[0];
    }

    public show(): void {
        if (!this.modelLoaded && this.url) this.loadModel(this.url);
        this.resetGeometry();
        this._show();
    }

    /* ------------------ VARIANTS ------------------ */

    public setVariants(variants: IVariantModel[]): void {
        this.variants = variants;
        variants.forEach((variant) => {
            variant.id = getRandomString();
        });
    }

    private fixVariant(variant: IVariantModel): IVariantModel {
        if (!variant) return variant;
        if (!variant.variantMeshesList) {
            const variantSearch = find(this.variants, (v) => v.name === variant.name);
            if (variantSearch) variant = variantSearch;
        }
        if (!variant.variantMeshesList) variant.variantMeshesList = [];
        return variant;
    }

    public findVariantById(id: string): IVariantModel {
        return this.variants.find((x) => x.id === id);
    }

    public setVariantByName(name: string, animate?: boolean): void {
        const variant = this.getVariantByName(name);
        if (variant) {
            this.setVariant(variant, animate);
        } else {
            this.variant = null;
            this.resetAllVariantMeshes(); // Means default variant
        }
    }

    public resetAllVariantMeshes(): void {
        const meshes = this.getVariantMeshesPropertiesList();
        meshes.forEach((mesh) => {
            this.setVariantMeshes({
                meshes: [mesh.meshes[0]],
                material: null
            }, false);
        });
    }

    public isVariantMeshesInVariantModel(
        variantName: string,
        variantMeshes: IVariantMeshes
    ): boolean {
        const variant = this.getVariantByName(variantName);
        const variantMeshesFound = find(variant.variantMeshesList,
            (vm) => isEqual(vm, variantMeshes));
        return variantMeshesFound !== undefined;
    }

    public getVariantMeshesInVariantModelByMeshes(
        variantName: string,
        meshes: string[]
    ): IVariantMeshes {
        const variant = this.getVariantByName(variantName);
        return (variant) ? find(variant.variantMeshesList, (vm) => isEqual(vm.meshes, meshes)) : {};
    }

    public addVariantMeshes(variantMeshes: IVariantMeshes, updateModel?: boolean): void {
        if (!this.doesVariantMeshesExist(variantMeshes)) {
            const { meshes } = variantMeshes;
            const variantMeshesMaterials = this.getVariantMeshesPropertiesByMeshes(meshes);
            if (variantMeshesMaterials) {
                if (variantMeshesMaterials.materials) {
                    variantMeshesMaterials.materials.push(variantMeshes.material);
                } else {
                    variantMeshesMaterials.materials = [variantMeshes.material];
                }
            } else {
                this.variantMeshesPropertiesList.push(
                    { meshes, materials: [variantMeshes.material] }
                );
            }
            if (updateModel) {
                this.setVariantMeshes({ meshes, material: variantMeshes.material }, true);
            }
        }
    }

    public removeVariantMeshes(variantMeshes: IVariantMeshes, updateModel?: boolean): void {
        if (this.doesVariantMeshesExist(variantMeshes)) {
            const { meshes } = variantMeshes;
            const variantMeshesMaterials = this.getVariantMeshesPropertiesByMeshes(meshes);
            remove(variantMeshesMaterials.materials, (m) => isEqual(m, variantMeshes.material));
            if (updateModel) this.setVariantMeshes({ meshes, material: null }, true);
        }
    }

    public addVariantMeshesInVariantModel(
        variantName: string,
        variantMeshes: IVariantMeshes,
        updateModel?: boolean
    ): void {
        const variant = this.getVariantByName(variantName);
        const { meshes } = variantMeshes;
        const cVM = this.getVariantMeshesInVariantModelByMeshes(variantName, meshes);
        if (cVM) {
            cVM.material = variantMeshes.material;
        } else {
            variant.variantMeshesList.push(variantMeshes);
        }
        if (updateModel) this.setVariantMeshes(variantMeshes, true);
    }

    public removeVariantMeshesInVariantModel(
        variantName: string,
        variantMeshes: IVariantMeshes,
        updateModel?: boolean
    ): void {
        const variant = this.getVariantByName(variantName);
        remove(variant.variantMeshesList, (vm) => isEqual(vm.meshes, variantMeshes.meshes));
        // Then clean variant if no more meshes in one variantMeshes due to previus remove
        remove(variant.variantMeshesList, (vm) => !vm.meshes.length);
        if (updateModel) {
            this.setVariantMeshes({ meshes: variantMeshes.meshes, material: null }, true);
        }
    }

    public setVariant(variant: IVariantModel, animate: boolean, doNotSave?: boolean): void {
        // Stop before change so that animation doesn't delete new cloned meshes
        this.variantAnimation.stop();
        variant = this.fixVariant(variant);
        if (this.variant) this.checkVariantChange(variant, animate);
        if (!doNotSave) this.variant = variant.name;
        if (!this.modelLoaded) return;
        if (!variant || variant.variantMeshesList.length === 0) {
            this.resetAllVariantMeshes();
        } else {
            this.setAllVariantMeshesMaterial(variant.variantMeshesList, animate);
            this.setAllVariantDecal(variant.variantTextList, variant.variantMaskList);
            if (animate) {
                this.animateVariant();
            } else {
                // Make sure, everything is remove if no animation
                this.removeAnimMesh();
                this._system.scene.render();
            }
        }
    }

    public getCurrentVariant(): IVariantModel {
        return this.getVariantByName(this.variant);
    }

    private checkVariantChange(variant: IVariantModel, animate: boolean) {
        const clonedVariantMeshList = cloneDeep(this.variantMeshesList);
        if (clonedVariantMeshList.length) {
            clonedVariantMeshList.forEach((variantMesh) => {
                const { meshes } = variantMesh;
                if (variant && variant.variantMeshesList) {
                    const vML = variant.variantMeshesList;
                    const isInNewVariant = find(vML, (c) => isEqual(c.meshes, meshes));
                    if (!isInNewVariant) {
                        this._unsafeSetVariantMeshes(
                            { meshes, material: null },
                            animate
                        );
                    }
                } else {
                    this._unsafeSetVariantMeshes(
                        { meshes, material: null },
                        animate
                    );
                }
            });
        }
    }

    protected checkMaterialChange(materialKey: string): void {
        // Need to clone as there a remove call on array in setVariantMesh
        const variantMeshListCopy = cloneDeep(this.variantMeshList);
        variantMeshListCopy.forEach((variantMesh) => {
            const { material } = variantMesh;
            if (materialKey === material) {
                this.setVariantMesh(variantMesh, false);
            }
        });

        const patternTexts = this.getPatternTexts();
        patternTexts.forEach((patternText) => {
            const variant = patternText.getVariant() as IVariantText;
            if (variant.material === materialKey) patternText.setMaterial(materialKey);
        });
    }

    public addVariant(variant: IVariantModel): void {
        if (!variant.id) variant.id = getRandomString();
        this.variants.push(variant);
    }

    public removeVariant(variant: IVariantModel): void {
        remove(this.variants, (v) => v.id === variant.id);
    }

    public getVariants(): IVariantModel[] {
        return [...this.variants];
    }

    public getVariantByName(name: string): IVariantModel {
        return find(this.variants, (v) => name === v.name);
    }

    public removeMaterialFromVariants(materialKey: string): void {
        this.variants.forEach((variant) => {
            remove(variant.variantMeshesList, (vm) => vm.material === materialKey);
        });
        this.variantMeshesPropertiesList.forEach((variant) => {
            remove(variant.materials, (m) => m === materialKey);
        });
    }
}
